import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Calendar, Clock, Plus, Trash2, Send } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface Lead {
  id: string;
  name: string | null;
  email: string | null;
  company: string | null;
}

interface EmailTemplate {
  id: string;
  template_name: string;
  template_type: string;
}

interface DripEmail {
  template_id: string;
  delay_days: number;
  custom_message: string;
}

export const CampaignScheduler = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedLead, setSelectedLead] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [customMessage, setCustomMessage] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [dripEmails, setDripEmails] = useState<DripEmail[]>([
    { template_id: '', delay_days: 0, custom_message: '' }
  ]);

  useEffect(() => {
    fetchLeads();
    fetchTemplates();
  }, []);

  const fetchLeads = async () => {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setLeads(data);
    }
  };

  const fetchTemplates = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('email_templates')
      .select('id, template_name, template_type')
      .eq('agent_id', user.id);

    if (!error && data) {
      setTemplates(data);
    }
  };

  const handleScheduleOneTime = async () => {
    if (!selectedLead || !selectedTemplate || !scheduledDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const scheduledFor = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();

    const { error } = await supabase.functions.invoke('send-scheduled-emails', {
      body: {
        action: 'schedule_email',
        scheduleData: {
          agent_id: user.id,
          lead_id: selectedLead,
          template_id: selectedTemplate,
          scheduled_for: scheduledFor,
          custom_message: customMessage,
        }
      }
    });

    if (error) {
      toast.error('Failed to schedule email');
      return;
    }

    toast.success('Email scheduled successfully');
    resetForm();
  };

  const handleCreateDripCampaign = async () => {
    if (!selectedLead || !campaignName || dripEmails.some(e => !e.template_id)) {
      toast.error('Please fill in all required fields');
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const baseDate = new Date();
    const emails = dripEmails.map((email) => {
      const scheduledFor = new Date(baseDate);
      scheduledFor.setDate(scheduledFor.getDate() + email.delay_days);
      
      return {
        lead_id: selectedLead,
        template_id: email.template_id,
        scheduled_for: scheduledFor.toISOString(),
        custom_message: email.custom_message,
      };
    });

    const { error } = await supabase.functions.invoke('send-scheduled-emails', {
      body: {
        action: 'create_drip_campaign',
        campaignData: {
          agent_id: user.id,
          campaign_name: campaignName,
          emails,
        }
      }
    });

    if (error) {
      toast.error('Failed to create drip campaign');
      return;
    }

    toast.success('Drip campaign created successfully');
    resetDripForm();
  };

  const addDripEmail = () => {
    setDripEmails([...dripEmails, { template_id: '', delay_days: 0, custom_message: '' }]);
  };

  const removeDripEmail = (index: number) => {
    setDripEmails(dripEmails.filter((_, i) => i !== index));
  };

  const updateDripEmail = (index: number, field: keyof DripEmail, value: any) => {
    const updated = [...dripEmails];
    updated[index] = { ...updated[index], [field]: value };
    setDripEmails(updated);
  };

  const resetForm = () => {
    setSelectedLead('');
    setSelectedTemplate('');
    setScheduledDate('');
    setScheduledTime('09:00');
    setCustomMessage('');
  };

  const resetDripForm = () => {
    setSelectedLead('');
    setCampaignName('');
    setDripEmails([{ template_id: '', delay_days: 0, custom_message: '' }]);
  };

  return (
    <Tabs defaultValue="one-time" className="w-full">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="one-time">
          <Clock className="w-4 h-4 mr-2" />
          One-Time Schedule
        </TabsTrigger>
        <TabsTrigger value="drip">
          <Calendar className="w-4 h-4 mr-2" />
          Drip Campaign
        </TabsTrigger>
      </TabsList>

      <TabsContent value="one-time" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Schedule Single Email</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Select Lead</Label>
              <Select value={selectedLead} onValueChange={setSelectedLead}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a lead..." />
                </SelectTrigger>
                <SelectContent>
                  {leads.map((lead) => (
                    <SelectItem key={lead.id} value={lead.id}>
                      {lead.name || lead.email} - {lead.company || 'No company'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Email Template</Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.template_name} ({template.template_type.replace('_', ' ')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label>Time</Label>
                <Input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label>Custom Message (Optional)</Label>
              <Input
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Add a personalized message..."
              />
            </div>

            <Button onClick={handleScheduleOneTime} className="w-full">
              <Send className="w-4 h-4 mr-2" />
              Schedule Email
            </Button>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="drip" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Create Drip Campaign</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Campaign Name</Label>
              <Input
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="e.g., New Lead Nurture Sequence"
              />
            </div>

            <div>
              <Label>Select Lead</Label>
              <Select value={selectedLead} onValueChange={setSelectedLead}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a lead..." />
                </SelectTrigger>
                <SelectContent>
                  {leads.map((lead) => (
                    <SelectItem key={lead.id} value={lead.id}>
                      {lead.name || lead.email} - {lead.company || 'No company'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label>Email Sequence</Label>
                <Button variant="outline" size="sm" onClick={addDripEmail}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Email
                </Button>
              </div>

              {dripEmails.map((email, index) => (
                <Card key={index}>
                  <CardContent className="pt-6 space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold">Email {index + 1}</h4>
                      {dripEmails.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDripEmail(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    <div>
                      <Label>Template</Label>
                      <Select
                        value={email.template_id}
                        onValueChange={(value) => updateDripEmail(index, 'template_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose template..." />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map((template) => (
                            <SelectItem key={template.id} value={template.id}>
                              {template.template_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Days after previous email</Label>
                      <Input
                        type="number"
                        min="0"
                        value={email.delay_days}
                        onChange={(e) => updateDripEmail(index, 'delay_days', parseInt(e.target.value))}
                      />
                    </div>

                    <div>
                      <Label>Custom Message (Optional)</Label>
                      <Input
                        value={email.custom_message}
                        onChange={(e) => updateDripEmail(index, 'custom_message', e.target.value)}
                        placeholder="Add personalized message..."
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Button onClick={handleCreateDripCampaign} className="w-full">
              <Calendar className="w-4 h-4 mr-2" />
              Create Drip Campaign
            </Button>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};